/**
 * Zapier Integration Sprint · Phase 1 — Super Admin Console
 *
 * /admin/zapier — dashboard to manage Zapier / CRM integrations:
 *   • Create integrations (API key or OAuth 2.0) with CRM sync targets
 *   • Rotate keys / secrets, revoke integrations
 *   • Subscribe endpoints to events, toggle & delete them
 *   • Watch webhook deliveries + retry failed ones
 *   • Live "Latest 20 events" feed with delivery stats
 *   • Integration health tile (per-integration status + 24h roll-up)
 *   • Fire a test event
 *
 * Backed by /api/admin/zapier/* (Super Admin only).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, KeyRound, Plus, RefreshCcw, RotateCw, Trash2, ShieldOff,
  ShieldCheck, Copy, Send, Webhook, ChevronRight, ChevronDown,
  Activity, Loader2, Radio, CheckCircle2, XCircle, AlertTriangle,
  Link2, Settings,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import MarketplaceTab from './zapier/MarketplaceTab';
import AlertsTab from './zapier/AlertsTab';
import InboundEventsTab from './zapier/InboundEventsTab';
import FieldMappingDialog from './zapier/FieldMappingDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Common Zapier event types the UI advertises for one-click subscription.
const COMMON_EVENTS = [
  '*',
  'lead.created', 'lead.updated',
  'vendor.signup', 'vendor.approved', 'vendor.rejected',
  'client.signup', 'talent.signup', 'agency.signup',
  'event.created',
  'shift.created', 'shift.published', 'shift.filled', 'shift.canceled',
  'booking.confirmed', 'check.in', 'check.out',
  'timesheet.approved',
  'invoice.created', 'invoice.paid',
  'review.submitted',
  'note.added',
];

const STATUS_STYLES = {
  delivered: 'bg-emerald-100 text-emerald-800',
  pending:   'bg-slate-100 text-slate-700',
  retrying:  'bg-amber-100 text-amber-900',
  failed:    'bg-rose-100 text-rose-800',
  dead:      'bg-rose-200 text-rose-900',
};

const HEALTH_STYLES = {
  healthy:  'bg-emerald-100 text-emerald-800 border-emerald-300',
  degraded: 'bg-amber-100 text-amber-900 border-amber-300',
  idle:     'bg-slate-100 text-slate-700 border-slate-300',
  revoked:  'bg-rose-100 text-rose-800 border-rose-300',
};

function copy(text) {
  navigator.clipboard?.writeText(text).then(
    () => toast.success('Copied to clipboard'),
    () => toast.error('Copy failed — select and copy manually'),
  );
}

function StatusPill({ status }) {
  const style = STATUS_STYLES[status] || 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${style}`} data-testid={`delivery-status-${status}`}>
      {status}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// CRM MULTI-SELECT
// ─────────────────────────────────────────────────────────────

function CRMSelector({ adapters, selected, onChange, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testIdPrefix}-crm-selector`}>
      {adapters.map((a) => {
        const on = selected.includes(a.key);
        return (
          <button
            key={a.key}
            onClick={() =>
              onChange(on ? selected.filter((k) => k !== a.key) : [...selected, a.key])
            }
            className={`px-2.5 py-1 rounded-full text-xs border transition flex items-center gap-1.5 ${
              on
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'
            }`}
            data-testid={`${testIdPrefix}-crm-${a.key}`}
            title={a.docs_url}
          >
            {a.display_name}
            {a.live ? (
              <span className="text-emerald-300 text-[10px]">live</span>
            ) : (
              <span className="opacity-60 text-[10px]">mock</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CREATE INTEGRATION
// ─────────────────────────────────────────────────────────────

function CreateIntegrationCard({ adapters, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [authType, setAuthType] = useState('api_key');
  const [syncTargets, setSyncTargets] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (name.trim().length < 2) { toast.error('Integration name is required'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/zapier/integrations`,
        {
          name: name.trim(),
          description: description.trim() || null,
          auth_type: authType,
          sync_targets: syncTargets,
        },
        AUTH(),
      );
      onCreated(data);
      setName(''); setDescription(''); setSyncTargets([]);
      toast.success('Integration created — copy the raw key now');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5" data-testid="zapier-create-integration-card">
      <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
        <Plus className="w-5 h-5 text-indigo-600" /> New Integration
      </h3>
      <div className="space-y-3">
        <Input
          data-testid="zapier-new-name"
          placeholder="Integration name (e.g. Zapier — Builder Prime)"
          value={name} onChange={(e) => setName(e.target.value)}
        />
        <Textarea
          data-testid="zapier-new-description"
          placeholder="Optional description / owner / support contact"
          value={description} rows={2}
          onChange={(e) => setDescription(e.target.value)}
        />
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="radio" data-testid="zapier-auth-api-key"
              checked={authType === 'api_key'} onChange={() => setAuthType('api_key')} />
            API key
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="radio" data-testid="zapier-auth-oauth2"
              checked={authType === 'oauth2'} onChange={() => setAuthType('oauth2')} />
            OAuth 2.0
          </label>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1.5">
            CRM sync targets <span className="font-normal normal-case text-slate-500">— leads captured through this integration are pushed to every selected CRM.</span>
          </div>
          <CRMSelector
            adapters={adapters} selected={syncTargets}
            onChange={setSyncTargets} testIdPrefix="new-integration"
          />
        </div>
        <Button data-testid="zapier-new-submit-btn" disabled={busy} onClick={submit} className="w-full">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
          Create Integration
        </Button>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// SHOW ONCE
// ─────────────────────────────────────────────────────────────

function ShowOncePanel({ payload, onDone }) {
  if (!payload) return null;
  const { raw_api_key, hmac_secret, name } = payload;
  return (
    <Card className="p-5 border-2 border-emerald-300 bg-emerald-50" data-testid="zapier-show-once-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-emerald-900 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" /> Save these — they won&apos;t be shown again
          </h3>
          <p className="text-sm text-emerald-800 mt-1">
            Integration <strong>{name}</strong> has been created. Paste the API
            key into Zapier and store the HMAC secret in your webhook receiver.
          </p>
        </div>
        <Button data-testid="zapier-show-once-close-btn" size="sm" variant="outline" onClick={onDone}>
          I&apos;ve saved them
        </Button>
      </div>
      <div className="grid md:grid-cols-2 gap-3 mt-4">
        {raw_api_key && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-emerald-800 mb-1">API Key</div>
            <div className="flex items-center gap-2">
              <code data-testid="zapier-show-once-api-key" className="flex-1 bg-white border border-emerald-200 rounded px-3 py-2 text-sm break-all">{raw_api_key}</code>
              <Button size="icon" variant="ghost" onClick={() => copy(raw_api_key)}><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
        {hmac_secret && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-emerald-800 mb-1">HMAC Signing Secret</div>
            <div className="flex items-center gap-2">
              <code data-testid="zapier-show-once-hmac" className="flex-1 bg-white border border-emerald-200 rounded px-3 py-2 text-sm break-all">{hmac_secret}</code>
              <Button size="icon" variant="ghost" onClick={() => copy(hmac_secret)}><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// HEALTH TILE
// ─────────────────────────────────────────────────────────────

function HealthTile({ health }) {
  if (!health) return null;
  const d = health.deliveries_24h || {};
  const crm = health.crm_syncs_24h || {};
  return (
    <Card className="p-5" data-testid="zapier-health-tile">
      <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
        <Activity className="w-5 h-5 text-indigo-600" /> Integration Health (24h)
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div>
          <div className="text-xs text-slate-500">Deliveries</div>
          <div className="text-2xl font-semibold" data-testid="health-deliveries-total">{d.total || 0}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Success rate</div>
          <div className="text-2xl font-semibold text-emerald-700" data-testid="health-success-rate">
            {(d.success_rate ?? 100).toFixed(1)}%
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Retrying now</div>
          <div className="text-2xl font-semibold text-amber-700" data-testid="health-retrying">{d.retrying_now || 0}</div>
        </div>
        <div>
          <div className="text-xs text-slate-500">Failed</div>
          <div className="text-2xl font-semibold text-rose-700" data-testid="health-failed">{d.failed || 0}</div>
        </div>
      </div>
      {Object.keys(crm).length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
            CRM sync (24h)
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(crm).map(([k, v]) => (
              <div key={k} className="rounded-lg border px-3 py-2 text-xs" data-testid={`health-crm-${k}`}>
                <div className="font-semibold">{k}</div>
                <div className="text-emerald-700">{v.synced || 0} synced</div>
                {(v.failed || 0) > 0 && <div className="text-rose-700">{v.failed} failed</div>}
                {v.avg_ms && <div className="text-slate-500">~{v.avg_ms}ms avg</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {health.integrations?.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
            Per-integration status
          </div>
          <div className="space-y-1.5">
            {health.integrations.map((i) => (
              <div key={i.id} className="flex items-center justify-between text-sm gap-2" data-testid={`health-integration-${i.id}`}>
                <span className="truncate flex-1">{i.name}</span>
                <span className="text-xs text-slate-500">{i.active_endpoints} ep</span>
                <span className={`px-2 py-0.5 rounded-full text-xs border ${HEALTH_STYLES[i.health]}`}>
                  {i.health}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// LIVE EVENTS FEED
// ─────────────────────────────────────────────────────────────

function EventsFeed({ events, onRefresh }) {
  return (
    <Card className="p-5" data-testid="zapier-events-feed">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Radio className="w-5 h-5 text-indigo-600" /> Latest 20 Events
        </h3>
        <Button size="sm" variant="outline" onClick={onRefresh} data-testid="events-refresh">
          <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>
      {(!events || events.length === 0) && (
        <div className="text-sm text-slate-500 text-center py-6" data-testid="events-empty">
          No events fired yet. Trigger a signup, shift or lead capture, then hit Refresh.
        </div>
      )}
      <div className="space-y-1.5 max-h-[500px] overflow-auto">
        {(events || []).map((e) => {
          const d = e.deliveries || {};
          const allOk = d.total === 0 || d.delivered === d.total;
          const anyFail = d.failed > 0;
          return (
            <div
              key={e.id}
              className="border rounded-lg px-3 py-2 text-sm hover:bg-slate-50 transition"
              data-testid={`event-row-${e.id}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                {anyFail ? (
                  <XCircle className="w-4 h-4 text-rose-600 flex-shrink-0" />
                ) : allOk ? (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                )}
                <span className="font-mono text-xs font-semibold">{e.event_type}</span>
                <span className="text-xs text-slate-500">
                  {new Date(e.created_at).toLocaleTimeString()}
                </span>
                {e.source_object_type && (
                  <Badge variant="outline" className="text-[10px]">
                    {e.source_object_type}
                  </Badge>
                )}
                {d.total > 0 && (
                  <span className="ml-auto text-xs">
                    <span className="text-emerald-700">{d.delivered}</span>
                    /{d.total} delivered
                    {d.retrying > 0 && <span className="text-amber-700"> · {d.retrying} retrying</span>}
                    {d.failed > 0 && <span className="text-rose-700"> · {d.failed} failed</span>}
                    {d.avg_ms != null && <span className="text-slate-500"> · {d.avg_ms}ms</span>}
                  </span>
                )}
                {d.total === 0 && (
                  <span className="ml-auto text-xs text-slate-400">no subscribers</span>
                )}
              </div>
              {e.source_object_id && (
                <div className="text-xs text-slate-500 mt-0.5 ml-6 truncate">
                  #{String(e.source_object_id).slice(0, 32)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// INTEGRATION ROW
// ─────────────────────────────────────────────────────────────

function IntegrationRow({ integration, adapters, onRefresh, onRotateKey, onConfigure }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [endpointUrl, setEndpointUrl] = useState('');
  const [endpointEvents, setEndpointEvents] = useState(['*']);
  const [syncEditing, setSyncEditing] = useState(false);
  const [syncTargets, setSyncTargets] = useState(integration.sync_targets || []);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/zapier/integrations/${integration.id}`, AUTH());
      setDetail(data);
      setSyncTargets(data.integration?.sync_targets || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
  }, [integration.id]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggleEvent = (evt) => {
    setEndpointEvents((prev) => prev.includes(evt) ? prev.filter((e) => e !== evt) : [...prev, evt]);
  };

  const addEndpoint = async () => {
    if (!/^https?:\/\//.test(endpointUrl)) { toast.error('Target URL must start with http(s)://'); return; }
    if (endpointEvents.length === 0) { toast.error('Pick at least one event type'); return; }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/zapier/integrations/${integration.id}/endpoints`,
        { target_url: endpointUrl, event_types: endpointEvents }, AUTH(),
      );
      toast.success('Endpoint subscribed');
      setEndpointUrl(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const toggleEndpoint = async (id) => {
    try { await axios.post(`${API}/admin/zapier/endpoints/${id}/toggle`, {}, AUTH()); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Toggle failed'); }
  };

  const removeEndpoint = async (id) => {
    if (!window.confirm('Delete this endpoint subscription?')) return;
    try { await axios.delete(`${API}/admin/zapier/endpoints/${id}`, AUTH()); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const rotateKey = async () => {
    if (!window.confirm('Rotate API key? The old key stops working immediately.')) return;
    try {
      const { data } = await axios.post(`${API}/admin/zapier/integrations/${integration.id}/rotate-key`, {}, AUTH());
      onRotateKey({ ...data, name: integration.name });
      toast.success('Key rotated — copy the new value');
    } catch (e) { toast.error(e.response?.data?.detail || 'Rotate failed'); }
  };

  const revoke = async () => {
    if (!window.confirm('REVOKE this integration and disable all its endpoints?')) return;
    try {
      await axios.post(`${API}/admin/zapier/integrations/${integration.id}/revoke`, {}, AUTH());
      toast.success('Integration revoked'); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Revoke failed'); }
  };

  const saveSyncTargets = async () => {
    try {
      await axios.post(
        `${API}/admin/zapier/integrations/${integration.id}/sync-targets`,
        { sync_targets: syncTargets }, AUTH(),
      );
      toast.success('CRM sync targets saved');
      setSyncEditing(false); load(); onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const retryDelivery = async (deliveryId) => {
    try {
      await axios.post(`${API}/admin/zapier/deliveries/${deliveryId}/retry`, {}, AUTH());
      toast.success('Redispatched'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
  };

  const status = integration.revoked_at ? 'revoked' : 'active';

  return (
    <Card className="p-4" data-testid={`zapier-integration-row-${integration.id}`}>
      <button className="w-full flex items-center gap-3 text-left" onClick={() => setOpen((o) => !o)}
              data-testid={`zapier-integration-toggle-${integration.id}`}>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold truncate">{integration.name}</span>
            <Badge variant={status === 'active' ? 'default' : 'destructive'}>{status}</Badge>
            <Badge variant="outline">{integration.auth_type}</Badge>
            {integration.financial_lockdown && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" /> lockdown
              </Badge>
            )}
            {(integration.sync_targets || []).map((t) => (
              <Badge key={t} variant="outline" className="flex items-center gap-1">
                <Link2 className="w-3 h-3" /> {t}
              </Badge>
            ))}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            key {integration.api_key_prefix || '—'}
            {' · '}used {integration.usage_count} time(s)
            {integration.last_used_at && ' · last ' + new Date(integration.last_used_at).toLocaleString()}
          </div>
        </div>
      </button>

      {open && (
        <div className="mt-4 border-t pt-4 space-y-4">
          {status === 'active' && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => onConfigure?.(integration)}
                      data-testid={`zapier-configure-${integration.id}`}>
                <Settings className="w-4 h-4 mr-1.5" /> Field Mapping &amp; Preferences
              </Button>
              <Button size="sm" variant="outline" onClick={rotateKey} data-testid={`zapier-rotate-key-${integration.id}`}>
                <RotateCw className="w-4 h-4 mr-1.5" /> Rotate Key
              </Button>
              <Button size="sm" variant="destructive" onClick={revoke} data-testid={`zapier-revoke-${integration.id}`}>
                <ShieldOff className="w-4 h-4 mr-1.5" /> Revoke
              </Button>
            </div>
          )}

          {status === 'active' && (
            <div className="bg-slate-50 rounded-lg p-3 space-y-2">
              <div className="text-sm font-semibold flex items-center gap-2">
                <Link2 className="w-4 h-4" /> CRM sync targets
                {!syncEditing && (
                  <Button size="sm" variant="ghost" onClick={() => setSyncEditing(true)}
                          data-testid={`zapier-edit-sync-${integration.id}`}>
                    Edit
                  </Button>
                )}
              </div>
              {syncEditing ? (
                <div className="space-y-2">
                  <CRMSelector adapters={adapters} selected={syncTargets}
                               onChange={setSyncTargets}
                               testIdPrefix={`edit-sync-${integration.id}`} />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveSyncTargets}
                            data-testid={`zapier-save-sync-${integration.id}`}>Save</Button>
                    <Button size="sm" variant="outline" onClick={() => {
                      setSyncEditing(false);
                      setSyncTargets(integration.sync_targets || []);
                    }}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-600">
                  {(integration.sync_targets || []).length === 0
                    ? <em className="text-slate-400">No CRMs connected — leads only live in iEvntür</em>
                    : (integration.sync_targets || []).join(', ')}
                </div>
              )}
            </div>
          )}

          {status === 'active' && (
            <div className="bg-slate-50 rounded-lg p-3 space-y-2">
              <div className="text-sm font-semibold flex items-center gap-2">
                <Webhook className="w-4 h-4" /> Subscribe a new endpoint
              </div>
              <Input data-testid={`zapier-new-endpoint-url-${integration.id}`}
                     placeholder="https://hooks.zapier.com/hooks/catch/..."
                     value={endpointUrl} onChange={(e) => setEndpointUrl(e.target.value)} />
              <div className="flex flex-wrap gap-1.5">
                {COMMON_EVENTS.map((evt) => (
                  <button key={evt} onClick={() => toggleEvent(evt)}
                    className={`px-2 py-0.5 rounded-full text-xs border transition ${
                      endpointEvents.includes(evt)
                        ? 'bg-indigo-600 text-white border-indigo-600'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'
                    }`}
                    data-testid={`zapier-event-chip-${evt}`}>
                    {evt}
                  </button>
                ))}
              </div>
              <Button size="sm" disabled={busy} onClick={addEndpoint}
                      data-testid={`zapier-add-endpoint-${integration.id}`}>
                <Plus className="w-4 h-4 mr-1.5" /> Subscribe
              </Button>
            </div>
          )}

          {detail?.endpoints?.length > 0 && (
            <div>
              <div className="text-sm font-semibold mb-2">Subscribed endpoints</div>
              <div className="space-y-2">
                {detail.endpoints.map((ep) => (
                  <div key={ep.id} className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2"
                       data-testid={`zapier-endpoint-row-${ep.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{ep.target_url}</div>
                      <div className="text-xs text-slate-500">
                        events: {ep.event_types.join(', ')}
                        {ep.failure_streak > 0 && (
                          <span className="ml-2 text-rose-600">{ep.failure_streak} recent failure(s)</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={ep.active ? 'default' : 'secondary'}>
                        {ep.active ? 'active' : 'off'}
                      </Badge>
                      <Button size="sm" variant="outline" onClick={() => toggleEndpoint(ep.id)}
                              data-testid={`zapier-toggle-endpoint-${ep.id}`}>
                        {ep.active ? 'Pause' : 'Resume'}
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => removeEndpoint(ep.id)}
                              data-testid={`zapier-delete-endpoint-${ep.id}`}>
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {detail?.recent_deliveries?.length > 0 && (
            <div>
              <div className="text-sm font-semibold mb-2">Recent deliveries</div>
              <div className="space-y-1.5 max-h-80 overflow-auto">
                {detail.recent_deliveries.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-3 border rounded px-3 py-2 text-sm"
                       data-testid={`zapier-delivery-row-${d.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <StatusPill status={d.status} />
                        <span className="font-medium">{d.event_type}</span>
                        <span className="text-xs text-slate-500">
                          attempts: {d.attempts}{d.response_code && ` · HTTP ${d.response_code}`}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {new Date(d.created_at).toLocaleString()}{d.response_body && ` · ${d.response_body}`}
                      </div>
                    </div>
                    {['retrying','dead','failed'].includes(d.status) && (
                      <Button size="sm" variant="outline" onClick={() => retryDelivery(d.id)}
                              data-testid={`zapier-retry-delivery-${d.id}`}>
                        <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Retry
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// TEST FIRE
// ─────────────────────────────────────────────────────────────

function TestFireCard({ onFired }) {
  const [eventType, setEventType] = useState('lead.created');
  const [payload, setPayload] = useState('{"first_name":"Test","email":"test@example.com"}');
  const [busy, setBusy] = useState(false);

  const fire = async () => {
    let parsed = {};
    try { parsed = JSON.parse(payload); } catch { toast.error('Payload must be valid JSON'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/zapier/test-fire`,
        { event_type: eventType, payload: parsed }, AUTH(),
      );
      toast.success(`Event fired · id ${data.event_id?.slice(0, 8)}…`);
      onFired?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Test fire failed'); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5" data-testid="zapier-test-fire-card">
      <h3 className="text-lg font-semibold flex items-center gap-2 mb-3">
        <Send className="w-5 h-5 text-indigo-600" /> Test-Fire an Event
      </h3>
      <div className="space-y-2">
        <Input data-testid="zapier-test-event-type" value={eventType}
               onChange={(e) => setEventType(e.target.value)} placeholder="event.type" />
        <Textarea data-testid="zapier-test-payload" value={payload}
                  onChange={(e) => setPayload(e.target.value)}
                  rows={4} className="font-mono text-xs" />
        <Button data-testid="zapier-test-fire-submit" onClick={fire} disabled={busy} className="w-full">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
          Fire Event
        </Button>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────

export default function ZapierIntegrations() {
  const [integrations, setIntegrations] = useState([]);
  const [adapters, setAdapters] = useState([]);
  const [events, setEvents] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnce, setShowOnce] = useState(null);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [configureIntegration, setConfigureIntegration] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const loadIntegrations = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/zapier/integrations?include_revoked=${includeRevoked}`, AUTH());
      setIntegrations(data.integrations || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [includeRevoked]);

  const loadAdapters = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/zapier/crm-adapters`, AUTH());
      setAdapters(data.adapters || []);
    } catch { /* soft-fail */ }
  }, []);

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/zapier/events?limit=20`, AUTH());
      setEvents(data.events || []);
    } catch { /* soft-fail */ }
  }, []);

  const loadHealth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/zapier/health`, AUTH());
      setHealth(data);
    } catch { /* soft-fail */ }
  }, []);

  const loadAll = useCallback(async () => {
    await Promise.all([loadIntegrations(), loadAdapters(), loadEvents(), loadHealth()]);
  }, [loadIntegrations, loadAdapters, loadEvents, loadHealth]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh feed every 15s while the page is visible.
  useEffect(() => {
    const int = setInterval(() => {
      if (document.visibilityState === 'visible') { loadEvents(); loadHealth(); }
    }, 15000);
    return () => clearInterval(int);
  }, [loadEvents, loadHealth]);

  const activeCount = useMemo(
    () => integrations.filter((i) => !i.revoked_at).length,
    [integrations],
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 space-y-6" data-testid="zapier-admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Zap className="w-8 h-8 text-indigo-600" /> Zapier &amp; CRM Integrations
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Manage Zapier connections, webhook subscriptions and CRM sync
            (Builder Prime, HubSpot, Salesforce, Zoho, Dynamics, Pipedrive,
            Follow Up Boss, GoHighLevel). Financial actions (payouts, refunds,
            payouts) are always blocked from Zapier tokens.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={includeRevoked}
                   onChange={(e) => setIncludeRevoked(e.target.checked)}
                   data-testid="zapier-include-revoked-toggle" />
            Show revoked
          </label>
          <Button size="sm" variant="outline" onClick={loadAll} data-testid="zapier-refresh-btn">
            <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {showOnce && (
        <ShowOncePanel payload={showOnce} onDone={() => { setShowOnce(null); loadAll(); }} />
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto" data-testid="zapier-tabs-list">
          <TabsTrigger value="overview" data-testid="zapier-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="zapier-tab-integrations">
            Integrations
            <Badge variant="secondary" className="ml-1.5 text-[10px]">{activeCount}</Badge>
          </TabsTrigger>
          <TabsTrigger value="marketplace" data-testid="zapier-tab-marketplace">Marketplace</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="zapier-tab-alerts">Ops Alerts</TabsTrigger>
          <TabsTrigger value="inbound" data-testid="zapier-tab-inbound">Inbound</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <HealthTile health={health} />
          <div className="grid md:grid-cols-2 gap-4">
            <CreateIntegrationCard adapters={adapters} onCreated={(d) => setShowOnce(d)} />
            <TestFireCard onFired={loadAll} />
          </div>
          <EventsFeed events={events} onRefresh={loadEvents} />
        </TabsContent>

        <TabsContent value="integrations" className="space-y-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-semibold">
              Integrations
              <span className="ml-2 text-sm font-normal text-slate-500">{activeCount} active</span>
            </h2>
          </div>

          {loading && (
            <div className="text-center py-10 text-slate-500" data-testid="zapier-loading">
              <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading integrations…
            </div>
          )}

          {!loading && integrations.length === 0 && (
            <Card className="p-8 text-center text-slate-500" data-testid="zapier-empty-state">
              <Zap className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              No integrations yet. Create one from the Overview tab to get an API key you can paste into Zapier.
            </Card>
          )}

          <div className="space-y-3">
            {integrations.map((i) => (
              <IntegrationRow key={i.id} integration={i} adapters={adapters}
                              onRefresh={loadAll}
                              onRotateKey={(p) => setShowOnce(p)}
                              onConfigure={setConfigureIntegration} />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="marketplace">
          <MarketplaceTab />
        </TabsContent>

        <TabsContent value="alerts">
          <AlertsTab />
        </TabsContent>

        <TabsContent value="inbound">
          <InboundEventsTab />
        </TabsContent>
      </Tabs>

      {configureIntegration && (
        <FieldMappingDialog
          integration={configureIntegration}
          onClose={() => setConfigureIntegration(null)}
          onSaved={loadAll}
        />
      )}
    </div>
  );
}
